/**
 * Pool Audit log (/pool/audit) — the SaaS-standard "every write + every sweep observation" tab, `_manage`-
 * gated (pool role owner OR admin) the SAME way Settings/System are: a page-level `usePoolAccess()` check
 * BEFORE the feed ever fires, rendering the app's generic RBAC-forbidden copy for a pool-Viewer or a
 * non-operator — never <PoolErrorState/>'s "not a pool operator" copy, which is reserved for the four
 * `_view` screens a Viewer CAN reach (see PoolSettingsPage's own banner for the same split). A direct URL
 * hit as a Viewer therefore renders the identical forbidden state Settings/System already do; there is no
 * separate BE round trip needed to prove that (though the underlying GET /audit is ALSO `_manage`-gated on
 * the server, so even a client-side bug here would still 403 rather than leak rows).
 *
 * Filters (email / actor / action / date range) all narrow the SAME server-side keyset feed
 * (usePoolAuditFeed) — changing any of them re-queries from page 1 (a new queryKey), never client-side
 * filters a cached page. Pagination is "Load more" via `before_id` (the last row's own id): the /audit
 * envelope carries no next_cursor/has_more flag, so a page shorter than the page size IS the end signal.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye } from "lucide-react";
import {
  Badge,
  Button,
  DateRangePicker,
  EmptyState,
  ErrorState,
  Input,
  Select,
  SkeletonRows,
  Table,
  type Column,
  type DateRange,
} from "@/components/ui";
import { AuditDetailModal } from "@/features/pool/AuditDetailModal";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import {
  AUDIT_ACTIONS,
  auditActionLabel,
  formatAuditDetails,
} from "@/features/pool/poolAuditFormat";
import {
  usePoolAccess,
  usePoolAccountLookup,
  usePoolAuditFeed,
  type PoolAuditFilters,
} from "@/features/pool/hooks";
import { formatInstant } from "@/lib/format";
import type { PoolAuditEntry } from "@/api/types";

export function PoolAuditPage() {
  const { t } = useTranslation();
  const accessQ = usePoolAccess();

  if (accessQ.isPending) return <SkeletonRows rows={6} />;
  if (accessQ.isError) {
    return <PoolErrorState error={accessQ.error} onRetry={() => void accessQ.refetch()} />;
  }

  // Binary, same as Settings/System (rev 6 R2): a Viewer never reaches this screen's content, and there
  // is no read-only middle state for it either.
  const canManage = accessQ.data.role === "owner" || accessQ.data.role === "admin";
  if (!canManage) {
    return <ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />;
  }

  return <PoolAuditContent />;
}

function PoolAuditContent() {
  const { t } = useTranslation();

  const [emailInput, setEmailInput] = useState("");
  const [email, setEmail] = useState("");
  const [actorInput, setActorInput] = useState("");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [range, setRange] = useState<DateRange>({ start: "", end: "" });
  const [selected, setSelected] = useState<PoolAuditEntry | null>(null);

  const filters: PoolAuditFilters = {
    ...(email ? { email } : {}),
    ...(actor ? { actor } : {}),
    ...(action ? { action } : {}),
    // Bare dates on the wire: /pool/audit's backend owns the day semantics (a plain YYYY-MM-DD =
    // that WHOLE Asia/Bangkok calendar day, both bounds inclusive) — pre-computing a next-day
    // datetime here would double-apply the inclusivity nudge (verify lens 21/7, the +1us overlap).
    ...(range.start ? { date_from: range.start } : {}),
    ...(range.end ? { date_to: range.end } : {}),
  };

  const feedQ = usePoolAuditFeed(filters);
  const rows = feedQ.data?.pages.flatMap((p) => p.audit) ?? [];
  // r10 22/7: resolves a raw `account_id` inside a details change line to "name (#number)" — see
  // poolAuditFormat's humanizeAccountId banner for the shipped-name-vs-cache precedence.
  const resolveAccount = usePoolAccountLookup();

  const hasFilters =
    email !== "" || actor !== "" || action !== "" || range.start !== "" || range.end !== "";

  function clearFilters() {
    setEmailInput("");
    setEmail("");
    setActorInput("");
    setActor("");
    setAction("");
    setRange({ start: "", end: "" });
  }

  // rev 8 (21/7, prod screenshot review): a null target_email used to ALWAYS print its own "—" line
  // even when an account line followed it ("— \n Acme Pool Co · #1000001") — a dangling dash above a
  // real value. Now: an account line present suppresses the email dash entirely (email-then-account when
  // both exist, account-only when just the account exists); only when NEITHER exists does a single "—"
  // print, and the account line's own em dash never doubles up with a printed email line.
  function targetCell(r: PoolAuditEntry) {
    const accountBits = [
      r.target_account_name,
      r.target_account_number ? `#${r.target_account_number}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    if (!r.target_email && !accountBits) {
      return <span className="text-(--text)">—</span>;
    }
    return (
      <span className="flex flex-col leading-tight">
        {r.target_email && <span className="text-(--text)">{r.target_email}</span>}
        {accountBits && (
          <span className="text-(length:--text-2xs) text-(--text-muted)">{accountBits}</span>
        )}
      </span>
    );
  }

  function detailCell(r: PoolAuditEntry) {
    const lines = formatAuditDetails(r.details, resolveAccount);
    if (lines.length === 0) return <span className="text-(--text-subtle)">—</span>;
    const MAX_LINES = 2;
    const shown = lines.slice(0, MAX_LINES);
    const rest = lines.length - shown.length;
    return (
      <div className="flex max-w-xs flex-col leading-snug text-(length:--text-xs) text-(--text-muted)">
        {shown.map((line, i) => (
          <span key={i} className="truncate">
            {line}
          </span>
        ))}
        {rest > 0 && (
          <span className="text-(--text-subtle)">{t("pool.audit.moreLines", { count: rest })}</span>
        )}
      </div>
    );
  }

  const columns: Column<PoolAuditEntry>[] = [
    {
      key: "time",
      header: t("pool.audit.colTime"),
      numeric: true,
      cell: (r) => formatInstant(r.ts),
    },
    {
      key: "actor",
      header: t("pool.audit.colActor"),
      cell: (r) =>
        r.actor === "system" || r.actor === "sweep" ? (
          <Badge tone="neutral">{r.actor}</Badge>
        ) : (
          r.actor
        ),
    },
    {
      key: "action",
      header: t("pool.audit.colAction"),
      cell: (r) => <span className="text-(--text)">{auditActionLabel(t, r.action)}</span>,
    },
    {
      key: "target",
      header: t("pool.audit.colTarget"),
      cell: targetCell,
    },
    {
      key: "trigger",
      header: t("pool.audit.colTrigger"),
      cell: (r) => (r.trigger ? <Badge tone="neutral">{r.trigger}</Badge> : null),
    },
    {
      key: "detail",
      header: t("pool.audit.colDetail"),
      cell: detailCell,
    },
    {
      // r1 critique (24/7): the row's ONLY way into the detail modal used to be onRowClick on the whole
      // <tr> — a mouse-only "secret" full-row button (tabIndex + click/keydown but no role="button"/
      // "link" on a <tr> is invisible to a screen reader as an interactive control; Accounts/Users pages'
      // own convention is a declared link/button, never a silent row). This trailing cell is that
      // declared, keyboard-focusable control; onRowClick stays below purely as a mouse convenience on
      // top of it — stopPropagation keeps a click on the button itself from double-firing it.
      key: "actions",
      header: t("pool.audit.colActions"),
      align: "right",
      cell: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(r);
          }}
        >
          <Eye className="size-3.5" aria-hidden />
          {t("pool.audit.viewDetails")}
        </Button>
      ),
    },
  ];

  return (
    <section aria-labelledby="pool-audit-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1
          id="pool-audit-title"
          className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
        >
          {t("pool.audit.title")}
        </h1>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("pool.audit.subtitle")}</p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <form
          className="flex flex-col gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            setEmail(emailInput.trim());
          }}
        >
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.audit.filterEmailLabel")}
          </span>
          <Input
            value={emailInput}
            onChange={(ev) => setEmailInput(ev.target.value)}
            onBlur={() => setEmail(emailInput.trim())}
            placeholder={t("pool.audit.filterEmailPlaceholder")}
            type="email"
            className="w-56"
            aria-label={t("pool.audit.filterEmailLabel")}
          />
        </form>

        <form
          className="flex flex-col gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            setActor(actorInput.trim());
          }}
        >
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.audit.filterActorLabel")}
          </span>
          <Input
            value={actorInput}
            onChange={(ev) => setActorInput(ev.target.value)}
            onBlur={() => setActor(actorInput.trim())}
            placeholder={t("pool.audit.filterActorPlaceholder")}
            type="email"
            className="w-56"
            aria-label={t("pool.audit.filterActorLabel")}
          />
        </form>

        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.audit.filterActionLabel")}
          </span>
          <Select
            value={action}
            onChange={setAction}
            options={[
              { value: "", label: t("pool.audit.allActions") },
              ...AUDIT_ACTIONS.map((a) => ({ value: a, label: auditActionLabel(t, a) })),
            ]}
            aria-label={t("pool.audit.filterActionLabel")}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("activity.from")} — {t("activity.to")}
          </span>
          <DateRangePicker
            value={range}
            onChange={setRange}
            aria-label={`${t("activity.from")} — ${t("activity.to")}`}
          />
        </label>

        {hasFilters && (
          <Button variant="ghost" onClick={clearFilters}>
            {t("activity.clear")}
          </Button>
        )}
      </div>

      {feedQ.isPending ? (
        <SkeletonRows rows={6} />
      ) : feedQ.isError ? (
        <PoolErrorState error={feedQ.error} onRetry={() => void feedQ.refetch()} />
      ) : (
        <div className="flex flex-col gap-3">
          <Table
            columns={columns}
            rows={rows}
            rowKey={(r) => String(r.id)}
            caption={t("pool.audit.title")}
            onRowClick={setSelected}
            paginated={false}
            empty={
              <EmptyState title={t("pool.audit.emptyTitle")} body={t("pool.audit.emptyBody")} />
            }
          />
          {rows.length > 0 && (
            <div className="flex justify-center">
              {feedQ.hasNextPage ? (
                <Button
                  variant="secondary"
                  loading={feedQ.isFetchingNextPage}
                  onClick={() => feedQ.fetchNextPage()}
                >
                  {t("activity.loadMore")}
                </Button>
              ) : (
                <span className="text-(length:--text-2xs) text-(--text-subtle)">
                  {t("activity.end")}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {selected && (
        <AuditDetailModal
          entry={selected}
          resolveAccount={resolveAccount}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}
